import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faFileExcel,
  faFilterCircleXmark,
  faHand,
  faInbox,
  faPlus,
  faUser,
  faUserMinus,
} from "@fortawesome/free-solid-svg-icons";

import { crmPostventaRolloApi } from "./crmPostventaRolloApi";
import { openAssignResponsibleDialog } from "../crmpostventa-v2/assignResponsibleDialog";
import {
  EMPTY_FILTERS,
  PAGE_SIZE,
  VISTAS,
  buildExportQuery,
  buildListQuery,
  countActiveFilters,
  entregadasFilters,
  resetViewFilters,
} from "./rolloFilters";
import { demoraDays, formatGestiones, todayIso } from "./rolloFormat";
import { fallbackExportName, saveBlob, truncationMessage } from "./rolloExport";
import KpiCard from "./components/KpiCard";
import SegmentedControl, { segmentId } from "./components/SegmentedControl";
import FilterBar from "./components/FilterBar";
import EmptyState, { LoadError } from "./components/EmptyState";
import QueueSkeleton from "./components/Skeleton";
import Pagination from "./components/Pagination";
import Alert from "./components/Alert";
import RowActions, { NO_MANAGE_TITLE } from "./components/RowActions";
import { ACTIVAS_COLUMNS, ActivasTable, FINALIZADAS_COLUMNS, FinalizadasTable } from "./components/QueueTable";
import QueueCards from "./components/QueueCards";
import { isMine } from "./components/rolloCells";
import NuevaGestionModal from "./nuevaGestion/NuevaGestionModal";
import GestionarModal from "./gestionar/GestionarModal";
import DetalleModal from "./detalle/DetalleModal";
import { isClosed } from "./gestionar/gestionarModel";
import "./rollo.css";

const SEARCH_DEBOUNCE_MS = 300;
const MOBILE_QUERY = "(max-width: 900px)";
const RESULTS_ID = "crm-rollo-results";
const TABS_ID = "crm-rollo-vista";
const SWAL_TITLE = "Rollo";
// The new (or pointed) row keeps its highlight this long (design RolloNewSelect).
const SPOTLIGHT_MS = 8000;
const TOAST_MS = 5000;

const readMobile = () =>
  typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia(MOBILE_QUERY).matches;

// Mobile layout (cards and folded filters) at 900 px or less.
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(readMobile);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const media = window.matchMedia(MOBILE_QUERY);
    const handleChange = (event) => setIsMobile(event.matches);
    setIsMobile(media.matches);

    if (typeof media.addEventListener === "function") {
      media.addEventListener("change", handleChange);
      return () => media.removeEventListener("change", handleChange);
    }

    media.addListener?.(handleChange);
    return () => media.removeListener?.(handleChange);
  }, []);

  return isMobile;
}

/**
 * Rollo queue of CRM Postventa › Operaciones › Gestión (design RolloQueue / RolloFinalizadas / RolloMobile):
 * header with "Nueva gestión", KPIs, Activas | Finalizadas, filters, table or cards and pagination.
 *
 * "Nueva gestión" opens NuevaGestionModal, "Gestionar" (row or mobile card) opens GestionarModal and "Ver detalle"
 * (Finalizadas rows and the Activas row menu) opens DetalleModal for that row. Finalizadas has "Exportar Excel".
 * The list response carries usuarioActual { usuarioId, nombre, esAdmin, puedeGestionar }: it marks the user's
 * own rows ("Vos", "Ya es tuya") and hides or disables what the user cannot do. The server still enforces
 * every rule, so without usuarioActual the controls stay available and its errors are shown. Demora also comes
 * from the server: each row's demorada flag and diasParaDemora for the labels.
 */
export default function RolloPanel({ darkMode = false }) {
  const isMobile = useIsMobile();
  const [vista, setVista] = useState(VISTAS.ACTIVAS);
  const [searchValue, setSearchValue] = useState("");
  const [texto, setTexto] = useState("");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [pagina, setPagina] = useState(1);
  const [reloadKey, setReloadKey] = useState(0);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [menuId, setMenuId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [nuevaOpen, setNuevaOpen] = useState(false);
  // Rows whose "Gestionar" / "Detalle" modal is open.
  const [gestionarItem, setGestionarItem] = useState(null);
  const [detalleItem, setDetalleItem] = useState(null);
  // spotlight: row highlighted for a while ({ id, isNew }).
  const [spotlight, setSpotlight] = useState(null);
  // pin: the new gestión shown first in Activas until the query changes ({ id, row, after }). row is its last
  // detail, used only while the loaded page does not include it; after is the last list request started before
  // the pin, so only newer list responses refresh it.
  const [pin, setPin] = useState(null);
  const [exporting, setExporting] = useState(false);
  // Warning of the last export when the server cut it at its row limit.
  const [exportNotice, setExportNotice] = useState(null);
  const requestRef = useRef(0);
  const pinRequestRef = useRef(0);
  const mountedRef = useRef(true);
  const resultsRef = useRef(null);
  const nuevaButtonRef = useRef(null);

  const query = useMemo(() => buildListQuery({ vista, texto, filters, pagina }), [vista, texto, filters, pagina]);

  // Unpinning also invalidates a detail request of the pinned row that is still in flight.
  const clearPin = useCallback(() => {
    pinRequestRef.current += 1;
    setPin(null);
  }, []);

  // Only the latest request may update the screen: a slower older response never overwrites a newer one.
  const load = useCallback(async () => {
    requestRef.current += 1;
    const requestId = requestRef.current;
    setLoading(true);
    setError(null);
    try {
      const response = await crmPostventaRolloApi.listarGestiones(query);
      if (requestId !== requestRef.current) return;
      setResult({ vista: query.vista, requestId, response: response || {} });
    } catch (loadError) {
      if (requestId !== requestRef.current) return;
      setError(loadError);
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  }, [query]);

  // reloadKey forces a refresh with the same query (after creating a gestión).
  useEffect(() => {
    load();
  }, [load, reloadKey]);

  useEffect(
    () => () => {
      mountedRef.current = false;
      requestRef.current += 1;
      pinRequestRef.current += 1;
    },
    []
  );

  // Every list response after the pin refreshes it: the page row when the page has it, else its current detail
  // (a closed gestión leaves Activas, so it is unpinned). A detail that arrives after the pin changed is ignored.
  const pinId = pin?.id ?? null;
  const pinAfter = pin?.after ?? 0;
  useEffect(() => {
    if (!pinId || !result || result.requestId <= pinAfter || result.vista !== VISTAS.ACTIVAS) return;
    pinRequestRef.current += 1;
    const requestId = pinRequestRef.current;
    const keepPin = (update) => setPin((current) => (current?.id === pinId ? update(current) : current));

    if ((result.response?.items || []).some((item) => item.id === pinId)) {
      keepPin((current) => ({ ...current, row: null }));
      return;
    }

    crmPostventaRolloApi.obtenerGestion(pinId).then(
      (detalle) => {
        if (requestId !== pinRequestRef.current) return;
        const gestion = detalle?.gestion;
        const active = gestion?.id === pinId && gestion.activo !== false && !isClosed(gestion.estado);
        keepPin((current) => (active ? { ...current, row: gestion } : null));
      },
      () => {
        // The row still shows when it is on a reloaded page.
        if (requestId === pinRequestRef.current) keepPin((current) => ({ ...current, row: null }));
      }
    );
  }, [result, pinId, pinAfter]);

  // A new query makes the warning of a previous export stale.
  useEffect(() => {
    setExportNotice(null);
  }, [query]);

  useEffect(() => {
    const next = searchValue.trim();
    if (next === texto) return undefined;
    const handle = setTimeout(() => {
      setTexto(next);
      setPagina(1);
      clearPin();
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchValue, texto, clearPin]);

  useEffect(() => {
    if (!spotlight) return undefined;
    const handle = setTimeout(() => setSpotlight(null), SPOTLIGHT_MS);
    return () => clearTimeout(handle);
  }, [spotlight]);

  // A page can empty after an action or a change made by someone else: go back to the last page that exists.
  useEffect(() => {
    if (loading || !result || result.vista !== vista) return;
    const size = Number(result.response?.tamanoPagina) || PAGE_SIZE;
    const lastPage = Math.max(1, Math.ceil((Number(result.response?.total) || 0) / size));
    if (pagina > lastPage) setPagina(lastPage);
  }, [loading, result, vista, pagina]);

  const closeMenu = useCallback(() => setMenuId(null), []);

  const changeVista = (next) => {
    if (next === vista) return;
    setVista(next);
    setFilters((current) => resetViewFilters(current));
    setPagina(1);
    setMenuId(null);
    clearPin();
  };

  const changeFilter = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
    setPagina(1);
    clearPin();
  };

  const clearFilters = () => {
    setSearchValue("");
    setTexto("");
    setFilters(EMPTY_FILTERS);
    setPagina(1);
    clearPin();
  };

  const changePage = (next) => {
    setMenuId(null);
    setPagina(next);
    clearPin();
  };

  const response = result?.response;
  const responsables = response?.responsables || [];
  const usuarioActual = response?.usuarioActual || null;
  const currentUserId = usuarioActual?.usuarioId ?? null;
  // Unknown user (older server): keep every control and let the server answer.
  const canManage = usuarioActual ? usuarioActual.puedeGestionar !== false : true;
  const isAdmin = usuarioActual ? usuarioActual.esAdmin === true : true;

  // After creating: toast, back to Activas page 1 without filters or search, and refresh (KPIs included). The new
  // row goes first and highlighted. Clearing the filters keeps the pinned row from contradicting them; its detail
  // has the same shape as a queue row, so it can be shown when the reloaded page does not include it (the server
  // orders Activas by prioridad and antigüedad).
  const handleCreated = ({ gestionId, toast }) => {
    setNuevaOpen(false);
    Swal.fire({
      toast: true,
      position: "bottom-end",
      icon: "success",
      title: "Gestión creada",
      text: toast,
      showConfirmButton: false,
      showCloseButton: true,
      timer: TOAST_MS,
    });
    setVista(VISTAS.ACTIVAS);
    setFilters(EMPTY_FILTERS);
    setSearchValue("");
    setTexto("");
    setPagina(1);
    setMenuId(null);
    clearPin();
    setReloadKey((key) => key + 1);
    if (!gestionId) return;

    setSpotlight({ id: gestionId, isNew: true });
    setPin({ id: gestionId, row: null, after: requestRef.current });
  };

  // "Ver gestión abierta": the queue (Activas) filtered by the comercio's CUIT, with that row highlighted.
  const handleViewOpen = ({ gestionId, cuit }) => {
    setNuevaOpen(false);
    setVista(VISTAS.ACTIVAS);
    setFilters(EMPTY_FILTERS);
    setSearchValue(cuit);
    setTexto(cuit);
    setPagina(1);
    setMenuId(null);
    clearPin();
    setSpotlight(gestionId ? { id: gestionId, isNew: false } : null);
  };

  const openGestionar = useCallback((item) => {
    setMenuId(null);
    setGestionarItem(item);
  }, []);

  // After an action: toast, refresh the queue (KPIs included) and highlight the row while it is still active
  // (Entregado and Cancelado leave Activas, as the toast says). The row menu is already closed: opening Gestionar
  // (or the Detalle it can come from) closed it.
  const handleGestionDone = ({ gestionId, title, text }) => {
    setGestionarItem(null);
    Swal.fire({
      toast: true,
      position: "bottom-end",
      icon: "success",
      title,
      text,
      showConfirmButton: false,
      showCloseButton: true,
      timer: TOAST_MS,
    });
    setReloadKey((key) => key + 1);
    if (gestionId) setSpotlight({ id: gestionId, isNew: false });
  };

  const openDetalle = useCallback((item) => {
    setMenuId(null);
    setDetalleItem(item);
  }, []);

  // "Gestionar" in the footer of an active gestión's detail: the detail closes and Gestionar opens.
  const handleDetalleGestionar = (gestion) => {
    setDetalleItem(null);
    setGestionarItem(gestion);
  };

  // "Ver todas (N)" of the previous deliveries: Finalizadas of that comercio (its CUIT) with Resultado Entregado.
  const handleVerTodas = (gestion) => {
    const cuit = String(gestion?.cuit || "").trim();
    setDetalleItem(null);
    setVista(VISTAS.FINALIZADAS);
    setFilters(entregadasFilters());
    setSearchValue(cuit);
    setTexto(cuit);
    setPagina(1);
    setMenuId(null);
    clearPin();
  };

  // "Exportar Excel": the applied Finalizadas filters as .xlsx. The server caps the rows and says when it did and
  // how many it wrote; the toast counts those rows, or no number when a cut file does not say it.
  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    setExportNotice(null);
    try {
      const exported = await crmPostventaRolloApi.exportarFinalizadas(buildExportQuery({ texto, filters }));
      if (!mountedRef.current) return;
      saveBlob(exported.blob, exported.fileName || fallbackExportName());
      const matchingTotal = exported.total ?? (Number(result?.response?.total) || 0);
      const exportedRows = exported.rows ?? (exported.truncated ? null : matchingTotal);
      if (exported.truncated) setExportNotice(truncationMessage({ rows: exportedRows, total: matchingTotal }));
      Swal.fire({
        toast: true,
        position: "bottom-end",
        icon: "success",
        title: "Excel exportado",
        text: exportedRows === null ? undefined : formatGestiones(exportedRows),
        showConfirmButton: false,
        showCloseButton: true,
        timer: TOAST_MS,
      });
    } catch (exportError) {
      if (!mountedRef.current) return;
      Swal.fire({
        title: "No se pudo exportar",
        text: exportError?.message || "Probá de nuevo en unos segundos.",
        icon: "error",
        confirmButtonText: "Entendido",
      });
    } finally {
      if (mountedRef.current) setExporting(false);
    }
  };

  const runOwnership = async (item, request) => {
    setBusyId(item.id);
    try {
      await request();
      await load();
    } catch (actionError) {
      const conflict = actionError?.status === 409 || actionError?.status === 404;
      Swal.fire({
        title: SWAL_TITLE,
        text: actionError?.message || "No se pudo completar la acción.",
        icon: conflict ? "warning" : "error",
        confirmButtonText: "Entendido",
      });
      // The row changed or disappeared on the server: show the current queue.
      if (conflict) await load();
    } finally {
      setBusyId(null);
    }
  };

  const handleTomar = (item) =>
    runOwnership(item, () => crmPostventaRolloApi.tomar(item.id, { versionToken: item.versionToken }));

  const handleAsignar = async (item) => {
    const usuarioId = await openAssignResponsibleDialog({
      title: "Asignar gestión",
      responsables,
      currentResponsibleId: item.responsableUsuarioId,
      emptyText: "Todavía no hay responsables para asignar.",
    });
    if (!usuarioId) return;
    await runOwnership(item, () => crmPostventaRolloApi.asignar(item.id, { usuarioId, versionToken: item.versionToken }));
  };

  const handleLiberar = async (item) => {
    const confirmation = await Swal.fire({
      title: "Liberar gestión",
      text: "La gestión va a quedar sin responsable.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Liberar",
      cancelButtonText: "Cancelar",
    });
    if (!confirmation?.isConfirmed) return;
    await runOwnership(item, () => crmPostventaRolloApi.liberar(item.id, { versionToken: item.versionToken }));
  };

  // Same rules as the server: Tomar and Liberar need manage rights, Asignar is only for admins and a non-admin
  // can only release the gestiones they are responsible for.
  const liberarBlock = (mine, assigned) => {
    if (!assigned) return "La gestión no tiene responsable";
    if (!canManage) return NO_MANAGE_TITLE;
    if (!isAdmin && !mine) return "Solo el responsable o un administrador puede liberarla";
    return null;
  };

  const menuItemsFor = (item) => {
    const mine = isMine(item, currentUserId);
    const assigned = item.responsableUsuarioId !== null && item.responsableUsuarioId !== undefined;
    const liberarTitle = liberarBlock(mine, assigned);
    return [
      {
        key: "tomar",
        label: mine ? "Ya es tuya" : "Tomar",
        icon: faHand,
        disabled: mine || !canManage,
        title: !mine && !canManage ? NO_MANAGE_TITLE : undefined,
        onSelect: () => handleTomar(item),
      },
      isAdmin ? { key: "asignar", label: "Asignar…", icon: faUser, onSelect: () => handleAsignar(item) } : null,
      {
        key: "liberar",
        label: "Liberar",
        icon: faUserMinus,
        disabled: Boolean(liberarTitle),
        title: liberarTitle || undefined,
        onSelect: () => handleLiberar(item),
      },
      "separator",
      {
        key: "detalle",
        label: "Ver detalle",
        icon: faEye,
        onSelect: () => openDetalle(item),
      },
    ].filter(Boolean);
  };

  const shownVista = result?.vista;
  const finalizadas = shownVista === VISTAS.FINALIZADAS;

  const renderActions = (item, card = false) => (
    <RowActions
      item={item}
      card={card}
      finalizada={finalizadas}
      busy={busyId === item.id}
      canManage={canManage}
      onGestionar={openGestionar}
      onVerDetalle={openDetalle}
      menu={{
        label: `Más acciones para ${item.nombreComercio || "la gestión"}`,
        open: menuId === item.id,
        onToggle: () => setMenuId((current) => (current === item.id ? null : item.id)),
        onClose: closeMenu,
        items: menuItemsFor(item),
      }}
    />
  );

  // The header button is the focus target when the modal closes (the empty state repeats it).
  const renderNuevaGestionButton = (ref) => (
    <button
      ref={ref}
      type="button"
      className="crm-rollo__btn crm-rollo__btn--primary"
      disabled={!canManage}
      title={canManage ? undefined : NO_MANAGE_TITLE}
      onClick={() => setNuevaOpen(true)}
    >
      <FontAwesomeIcon icon={faPlus} aria-hidden="true" />
      Nueva gestión
    </button>
  );

  const today = todayIso();
  const activeCount = countActiveFilters(filters, vista);
  const hasFilters = activeCount > 0 || texto !== "";
  const canClear = activeCount > 0 || searchValue.trim() !== "";
  const isStale = !result || shownVista !== vista;
  const refreshing = loading && !isStale;
  const loadedItems = response?.items || [];
  const total = Number(response?.total) || 0;
  const kpis = response?.kpis;
  const conteos = response?.conteos;
  // Days of the demora rule as the server sends them; without them the labels go without a number.
  const diasParaDemora = demoraDays(response?.diasParaDemora);
  const canExport = !isStale && !error && finalizadas && total > 0 && !exporting;
  // The new gestión goes first in Activas: the reloaded row when the page has it, else the row from its detail.
  const pinnedRow = pin && !finalizadas ? loadedItems.find((item) => item.id === pin.id) || pin.row : null;
  const items = pinnedRow ? [pinnedRow, ...loadedItems.filter((item) => item.id !== pinnedRow.id)] : loadedItems;

  useEffect(() => {
    if (!spotlight || loading) return;
    const row = resultsRef.current?.querySelector(".is-spotlight");
    if (row && typeof row.scrollIntoView === "function") row.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [spotlight, loading, items.length]);

  const renderEmpty = () => {
    if (hasFilters) {
      return (
        <EmptyState
          icon={faFilterCircleXmark}
          title="No se encontraron gestiones con los filtros seleccionados"
          text="Probá con otro comercio, CUIT o terminal, o sacá algún filtro."
          action={
            <button type="button" className="crm-rollo__btn crm-rollo__btn--secondary" onClick={clearFilters}>
              Limpiar filtros
            </button>
          }
        />
      );
    }

    if (vista === VISTAS.ACTIVAS) {
      return (
        <EmptyState
          icon={faInbox}
          title="No hay gestiones de rollo activas"
          text="Cuando un comercio pida rollos, registrá el pedido para seguirlo hasta la entrega."
          action={renderNuevaGestionButton()}
        />
      );
    }

    return (
      <EmptyState
        icon={faInbox}
        title="Todavía no hay gestiones finalizadas"
        text="Las gestiones entregadas o canceladas aparecen acá."
      />
    );
  };

  const renderResults = () => {
    if (error) return <LoadError message={error.message} onRetry={load} />;
    if (isStale) {
      return <QueueSkeleton columns={vista === VISTAS.ACTIVAS ? ACTIVAS_COLUMNS : FINALIZADAS_COLUMNS} isMobile={isMobile} />;
    }
    if (!items.length) return renderEmpty();

    if (isMobile) {
      return (
        <QueueCards
          items={items}
          finalizadas={finalizadas}
          currentUserId={currentUserId}
          busyId={busyId}
          spotlight={spotlight}
          diasParaDemora={diasParaDemora}
          renderActions={renderActions}
        />
      );
    }

    return finalizadas ? (
      <FinalizadasTable items={items} currentUserId={currentUserId} renderActions={renderActions} />
    ) : (
      <ActivasTable
        items={items}
        currentUserId={currentUserId}
        today={today}
        busyId={busyId}
        spotlight={spotlight}
        diasParaDemora={diasParaDemora}
        renderActions={renderActions}
      />
    );
  };

  const meta =
    !isStale && !error && total > 0
      ? `${formatGestiones(total)} · ${finalizadas ? "más recientes primero" : "ordenadas por prioridad y antigüedad"}`
      : "";

  return (
    <section
      className={`crm-rollo${darkMode ? " is-dark" : ""}${isMobile ? " is-mobile" : ""}`}
      aria-labelledby="crm-rollo-title"
    >
      <div className="crm-rollo__top">
        <div>
          <h3 id="crm-rollo-title" className="crm-rollo__title">
            Rollo
          </h3>
          <p className="crm-rollo__desc">
            Pedidos de rollos de papel para las terminales de los comercios. Registrá cada pedido, seguilo hasta que el
            cadete lo entregue y cerralo.
          </p>
        </div>
        {renderNuevaGestionButton(nuevaButtonRef)}
      </div>

      <div className="crm-rollo__kpis">
        <KpiCard label="Activas" value={kpis?.activas} />
        <KpiCard label="Pendientes de preparar" value={kpis?.pendientesPreparar} />
        <KpiCard label="En camino" value={kpis?.enCamino} />
        <KpiCard
          label={diasParaDemora ? `Demoradas · +${diasParaDemora} días` : "Demoradas"}
          value={kpis?.demoradas}
          warn
          title={diasParaDemora ? `Más de ${diasParaDemora} días abiertas` : undefined}
        />
      </div>

      <div className="crm-rollo__bar">
        <SegmentedControl
          label="Ver gestiones"
          idPrefix={TABS_ID}
          controls={RESULTS_ID}
          value={vista}
          onChange={changeVista}
          options={[
            { value: VISTAS.ACTIVAS, label: "Activas", count: conteos?.activas },
            { value: VISTAS.FINALIZADAS, label: "Finalizadas", count: conteos?.finalizadas },
          ]}
        />
        <div className="crm-rollo__bar-end">
          <span className="crm-rollo__meta" aria-live="polite">
            {meta}
          </span>
          {vista === VISTAS.FINALIZADAS ? (
            <button
              type="button"
              className="crm-rollo__btn crm-rollo__btn--secondary crm-rollo__btn--sm"
              disabled={!canExport}
              aria-busy={exporting || undefined}
              onClick={handleExport}
            >
              {exporting ? (
                <>
                  <span className="crm-rollo__spinner" aria-hidden="true" />
                  Exportando…
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faFileExcel} aria-hidden="true" />
                  Exportar Excel
                </>
              )}
            </button>
          ) : null}
        </div>
      </div>

      {exportNotice ? (
        <Alert tone="warn" role="alert" title="Exportación parcial">
          {exportNotice}
        </Alert>
      ) : null}

      <FilterBar
        vista={vista}
        isMobile={isMobile}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        filters={filters}
        onFilterChange={changeFilter}
        responsables={responsables}
        provincias={response?.provincias}
        today={today}
        activeCount={activeCount}
        canClear={canClear}
        onClear={clearFilters}
        panelOpen={panelOpen}
        onTogglePanel={() => setPanelOpen((open) => !open)}
      />

      <div
        ref={resultsRef}
        id={RESULTS_ID}
        role="tabpanel"
        aria-labelledby={segmentId(TABS_ID, vista)}
        aria-busy={refreshing || undefined}
        className={`crm-rollo__results${refreshing ? " is-refreshing" : ""}`}
      >
        {renderResults()}
      </div>

      {!isStale && !error ? (
        <Pagination
          pagina={pagina}
          tamanoPagina={Number(response?.tamanoPagina) || PAGE_SIZE}
          total={total}
          disabled={loading}
          onChange={changePage}
        />
      ) : null}

      {nuevaOpen ? (
        <NuevaGestionModal
          darkMode={darkMode}
          asesorNombre={usuarioActual?.nombre}
          returnFocusRef={nuevaButtonRef}
          onClose={() => setNuevaOpen(false)}
          onCreated={handleCreated}
          onViewOpen={handleViewOpen}
        />
      ) : null}

      {gestionarItem ? (
        <GestionarModal
          key={gestionarItem.id}
          gestionId={gestionarItem.id}
          row={gestionarItem}
          darkMode={darkMode}
          diasParaDemora={diasParaDemora}
          onClose={() => setGestionarItem(null)}
          onDone={handleGestionDone}
        />
      ) : null}

      {detalleItem ? (
        <DetalleModal
          key={detalleItem.id}
          gestionId={detalleItem.id}
          row={detalleItem}
          currentUserId={currentUserId}
          canManage={canManage}
          darkMode={darkMode}
          onClose={() => setDetalleItem(null)}
          onGestionar={handleDetalleGestionar}
          onVerTodas={handleVerTodas}
        />
      ) : null}
    </section>
  );
}
